/**
 * Recognition in the browser: the osc-cpp pipeline compiled to WebAssembly,
 * with ONNX Runtime linked into it, driven page by page from a worker.
 *
 * The same shape as the native services (ADR 0010): a source blob in, one
 * MusicXML string out, an error with a message the catalog can show. What is
 * web-specific is where the pieces come from — the core and the weights are
 * fetched from this origin, so an image built without them (`build_docker.sh`
 * without WITH_RECOGNITION) still runs the catalog and reports recognition as
 * unavailable rather than breaking the page.
 *
 * PDFs are rasterised at 300 dpi with pdf.js, one page at a time and only
 * immediately before its run: a 300 dpi page is ~25 MB of RGB, and holding a
 * whole document of them is how a tab dies. The per-page scores are merged the
 * way `osc --merge-musicxml` merges them natively, by the merge oracle the
 * pipeline ships beside its wasm.
 */

import * as pdfjs from 'pdfjs-dist';
import { finishedLine, startedLine } from './progress';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

/** Same default as the pipeline's own process_pdf.sh. */
const DPI = 300;

/** `osc::par::kMaxThreads` — `osc_set_threads` clamps to it (wasm/ABI.md). */
export const MAX_THREADS = 32;

/**
 * Every core the browser admits to. The run blocks a worker, not the page, so
 * leaving a core spare buys nothing — and `hardwareConcurrency` is already a
 * conservative number, not the machine's real width.
 */
export function defaultThreads(): number {
  return Math.min(MAX_THREADS, Math.max(1, navigator.hardwareConcurrency || 4));
}

/** What a finished page actually ran as, as opposed to what it was asked for. */
export interface RunReport {
  threads: number;
  decodeUnits: number;
  secs: number;
}

/**
 * What a run reports while it is happening. The catalog turns these into the
 * line under a pending item; without them a page that takes minutes shows a
 * spinner and nothing else.
 */
export type RunActivity =
  | { kind: 'started'; threads: number; decodeUnits: number }
  | { kind: 'stage'; stage: number; current: number; total: number };

const base = (path: string): string =>
  new URL(`${import.meta.env.BASE_URL}${path}`, location.href).href;

const MODEL_URLS = {
  segnet: base('models/426/segnet.onnx'),
  encoder: base('models/426/encoder_pytorch_model_426-b6fd20809a8dcaf10dfd39a4ca4f64c6f056e644.onnx'),
  decoder: base('models/426/decoder_pytorch_model_426-b6fd20809a8dcaf10dfd39a4ca4f64c6f056e644.onnx'),
};

export class RecognitionUnavailable extends Error {
  constructor(why: string) {
    super(`Sheet music recognition is not available here: ${why}`);
    this.name = 'RecognitionUnavailable';
  }
}

interface Page {
  rgb: Uint8Array;
  w: number;
  h: number;
}

export class RecognitionService {
  private worker: Worker | undefined;

  /**
   * Threads for the next page. Clamped by the module itself, which returns what
   * it settled on via `onRun`.
   */
  threads: number = defaultThreads();

  /** Called with every finished page. */
  onRun: ((report: RunReport) => void) | undefined;

  /** Called as a page runs: once at the start, then on every stage event. */
  onActivity: ((activity: RunActivity) => void) | undefined;

  async recognizeImage(source: Blob): Promise<string> {
    await this.assertAvailable();
    return this.run(await rasterizeImage(source), 'page 1');
  }

  async recognizePdf(
    source: Blob,
    progress?: (page: number, total: number) => void,
  ): Promise<string> {
    await this.assertAvailable();
    const pdf = await pdfjs.getDocument({ data: await source.arrayBuffer() }).promise;
    const pages: string[] = [];
    for (let n = 1; n <= pdf.numPages; n += 1) {
      progress?.(n, pdf.numPages);
      pages.push(await this.run(await rasterizePdfPage(pdf, n), `page ${n}`));
    }
    if (pages.length === 1) return pages[0];
    const { mergeMusicXml } = await import(
      /* @vite-ignore */ base('osc/musicxml-merge.js')
    ) as { mergeMusicXml: (pages: string[]) => string };
    return mergeMusicXml(pages);
  }

  /**
   * Cross-origin isolation and the core itself, checked before a file is
   * rasterised rather than after: the failure is about the host, and the user
   * should not wait through a 300 dpi render to be told so.
   */
  private async assertAvailable(): Promise<void> {
    if (!self.crossOriginIsolated) {
      throw new RecognitionUnavailable(
        'this host does not send the COOP/COEP headers the recognition core needs',
      );
    }
    // The type, not just the status: a host with an SPA fallback answers a
    // missing file with index.html and a 200, so `ok` alone would let a bundle
    // built without the core get as far as importing HTML as a module — where
    // the browser reports a network failure and the real cause, an image built
    // without WITH_RECOGNITION, never appears.
    const response = await fetch(base('osc/osc-core-ort.mjs'), { method: 'HEAD' });
    const type = response.headers.get('content-type') ?? '';
    if (!response.ok || !/javascript|ecmascript/i.test(type)) {
      throw new RecognitionUnavailable(
        'this build does not carry the recognition core (the image was built ' +
        'without WITH_RECOGNITION=1)',
      );
    }
  }

  private run(page: Page, title: string): Promise<string> {
    const worker = (this.worker ??= new Worker(base('osc/omr-worker.js'), { type: 'module' }));
    return new Promise<string>((resolve, reject) => {
      worker.onmessage = (e: MessageEvent) => {
        const msg = e.data;
        if (msg.type === 'started') {
          const threads = msg.threads as number;
          const decodeUnits = msg.decodeUnits as number;
          console.info(startedLine(title, threads, decodeUnits, this.threads));
          this.onActivity?.({ kind: 'started', threads, decodeUnits });
        } else if (msg.type === 'progress') {
          this.onActivity?.({
            kind: 'stage',
            stage: msg.stage as number,
            current: msg.current as number,
            total: msg.total as number,
          });
        } else if (msg.type === 'done') {
          const report: RunReport = {
            threads: msg.threads as number,
            decodeUnits: msg.decodeUnits as number,
            secs: msg.secs as number,
          };
          console.info(finishedLine(title, report.threads, report.secs));
          this.onRun?.(report);
          resolve(msg.xml as string);
        } else if (msg.type === 'error') reject(new Error(msg.message as string));
      };
      worker.onerror = () => reject(new Error('the recognition worker stopped'));
      worker.postMessage(
        {
          type: 'run',
          rgb: page.rgb,
          h: page.h,
          w: page.w,
          title,
          modelUrls: MODEL_URLS,
          threads: this.threads,
        },
        // Transferred, not copied: a page is tens of megabytes.
        [page.rgb.buffer as ArrayBuffer],
      );
    });
  }
}

/**
 * Colour management and premultiplication are off on purpose: with them on, a
 * PNG carrying an iCCP profile comes out of the canvas with different bytes
 * than the native decode reads, and the score drifts from the native one for a
 * reason nobody would look for in a decoder.
 */
async function rasterizeImage(source: Blob): Promise<Page> {
  const bitmap = await createImageBitmap(source, {
    colorSpaceConversion: 'none',
    premultiplyAlpha: 'none',
  });
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d canvas to rasterise into');
  ctx.fillStyle = '#ffffff'; // a transparent PNG is paper, not a black page
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return toRgb(ctx, canvas.width, canvas.height);
}

async function rasterizePdfPage(
  pdf: pdfjs.PDFDocumentProxy,
  n: number,
): Promise<Page> {
  const page = await pdf.getPage(n);
  const viewport = page.getViewport({ scale: DPI / 72 });
  const canvas = new OffscreenCanvas(Math.round(viewport.width), Math.round(viewport.height));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d canvas to rasterise into');
  ctx.fillStyle = '#ffffff'; // PDF pages are transparent; the pipeline expects paper
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({
    canvas: canvas as unknown as HTMLCanvasElement,
    canvasContext: ctx as unknown as CanvasRenderingContext2D,
    viewport,
  }).promise;
  const rgb = toRgb(ctx, canvas.width, canvas.height);
  page.cleanup();
  return rgb;
}

/** RGBA from the canvas, RGB triplets for the pipeline. */
function toRgb(
  ctx: OffscreenCanvasRenderingContext2D,
  w: number,
  h: number,
): Page {
  const { data } = ctx.getImageData(0, 0, w, h);
  const rgb = new Uint8Array(w * h * 3);
  for (let i = 0, j = 0; i < data.length; i += 4, j += 3) {
    rgb[j] = data[i];
    rgb[j + 1] = data[i + 1];
    rgb[j + 2] = data[i + 2];
  }
  return { rgb, w, h };
}
